/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useState } from 'react';
import { Field } from 'react-final-form';
import { LinkButton, Text } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';

import { respondToAuthChallenge } from 'id-api/auth';
import { FormCard } from 'id-components/FormCard';
import { useLoginFlowSubmit, useChallengeCookie } from 'id-utils/login';
import { confirmPhoneNumberInputConfig } from 'id-constants/commonInputs';

import { ConfirmMFAOptOutModal } from './ConfirmMFAOptOutModal/ConfirmMFAOptOutModal';
import { phoneNumber } from './inputConfigs';

// used by calipers
const ID_EMAIL_MFA = 'it-EmailMFA';
const ID_SETUP_MFA_PHONE = 'it-SetupMFA-Phone';
const ID_OPT_OUT_MFA = 'it-OptOutMFA';

const styles = StyleSheet.create({
  footerWrapper: {
    flex: '1',
  },
  spacer: {
    ':not(:last-child)': {
      marginBottom: 16,
    },
  },
});

export namespace PhoneMFAForm {
  export interface Props {
    setUseEmailMFA: (bool: boolean) => void;
  }
}

export const PhoneMFAForm: React.FC<PhoneMFAForm.Props> = ({ setUseEmailMFA }) => {
  const { cookie } = useChallengeCookie();
  const [optOutModalOpen, setOptOutModalOpen] = useState<boolean>(false);
  const backSupported = cookie && cookie.returnToMFASetupSupported;
  const allowMfaOptOut = cookie?.allowMfaOptOut;
  const onMFAOptOut = useLoginFlowSubmit(respondToAuthChallenge, () => ({
    mfaType: 'NONE',
  }));
  const allowEmailMfa = cookie?.allowEmailMFA;
  const phoneFormValuesTransformer = (formValues: Identity.API.UpdateMFATypeChallenge) => ({
    mfaType: 'SMS_MFA',
    // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
    phoneNumber: `+1${formValues.phoneNumber}`,
  });
  const onPhoneMFASubmit = useLoginFlowSubmit(respondToAuthChallenge, phoneFormValuesTransformer);
  return (
    <div>
      <FormCard<Identity.API.UpdateMFATypeChallenge>
        headerLabel={
          <Text>
            Enter a mobile number where you can receive a SMS code. You will be texted a code to use each time you
            login.{' '}
          </Text>
        }
        headerTitle="Phone Verification"
        id={ID_SETUP_MFA_PHONE}
        onSubmit={onPhoneMFASubmit}
        cancelLabel={backSupported ? 'Back' : 'Cancel'}
        submitLabel="Complete activation"
      >
        <Field {...phoneNumber} />
        <div className={css(styles.spacer)}>
          <Field {...confirmPhoneNumberInputConfig} />
        </div>
        {allowEmailMfa && (
          <Text fontSize="sm" weight="medium">
            Don’t have a mobile phone number?{' '}
            <LinkButton
              btnSize="sm"
              id={ID_EMAIL_MFA}
              inline
              label="Setup email MFA"
              onClick={() => setUseEmailMFA(true)}
            />
          </Text>
        )}
        {allowMfaOptOut && (
          <LinkButton
            btnSize="sm"
            id={ID_OPT_OUT_MFA}
            inline
            label="Decline 2-step Verification"
            onClick={() => setOptOutModalOpen(true)}
          />
        )}
      </FormCard>
      <ConfirmMFAOptOutModal
        isOpen={optOutModalOpen}
        handleSubmit={() => onMFAOptOut({})}
        handleClose={() => setOptOutModalOpen(false)}
      />
    </div>
  );
};
